import { axiosAssessmentInstance } from "../AxiosConfiguration";

// Cache configuration
const FUNCTIONALITY_ACCESS_CACHE_KEY = 'functionality_access_cache';
const FUNCTIONALITY_ACCESS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

/**
 * Get cached functionality access from localStorage
 * @returns {Object|null} - Cached data or null if not found/expired
 */
const getCachedFunctionalityAccess = () => {
  try {
    const cached = localStorage.getItem(FUNCTIONALITY_ACCESS_CACHE_KEY);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is expired
    if (now - timestamp > FUNCTIONALITY_ACCESS_CACHE_TTL) {
      localStorage.removeItem(FUNCTIONALITY_ACCESS_CACHE_KEY);
      return null;
    }
    
    return data;
  } catch (error) {
    console.warn('Error reading functionality access cache:', error);
    return null;
  }
};

/**
 * Set functionality access cache in localStorage
 * @param {Object} data - The data to cache
 */
const setCachedFunctionalityAccess = (data) => {
  try {
    const cacheEntry = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(FUNCTIONALITY_ACCESS_CACHE_KEY, JSON.stringify(cacheEntry));
  } catch (error) {
    console.warn('Error setting functionality access cache:', error);
  }
};

/**
 * Clear functionality access cache
 */
export const clearFunctionalityAccessCache = () => {
  try {
    localStorage.removeItem(FUNCTIONALITY_ACCESS_CACHE_KEY);
  } catch (error) {
    console.warn('Error clearing functionality access cache:', error);
  }
};

/**
 * Get user's functionality access
 * Retrieves all active functionality access records for the authenticated user
 * @param {boolean} forceRefresh - Force refresh from API (default: false)
 * @returns {Promise<{user_id: string, has_unlimited_coaching: boolean, functionalities: Object}>}
 */
export const getFunctionalityAccess = async (forceRefresh = false) => {
  console.log('[FunctionalityAccess Service] getFunctionalityAccess called, forceRefresh:', forceRefresh);
  
  // Check cache first (unless force refresh)
  if (!forceRefresh) {
    const cached = getCachedFunctionalityAccess();
    if (cached) {
      console.log('[FunctionalityAccess Service] Returning cached data:', cached);
      return cached;
    }
  }

  try {
    console.log('[FunctionalityAccess Service] Making API call to /api/functionality-access/me');
    const response = await axiosAssessmentInstance.get('/api/functionality-access/me');
    console.log('[FunctionalityAccess Service] API response:', response.data);
    const data = response.data?.payload || {
      user_id: '',
      has_unlimited_coaching: false,
      functionalities: {}
    };
    
    // Cache the response
    setCachedFunctionalityAccess(data);
    
    return data;
  } catch (error) {
    console.error('[FunctionalityAccess Service] Error fetching functionality access:', error);
    throw error;
  }
};

/**
 * Get coaching access details
 * Retrieves detailed coaching access information with convenience fields
 * @returns {Promise<{has_access: boolean, unlimited: boolean, available_seconds: number, available_minutes: number, previous_minutes: number, unit: string, status?: string}>}
 */
export const getCoachingAccess = async () => {
  try {
    const response = await axiosAssessmentInstance.get('/api/functionality-access/coaching');
    return response.data?.payload || {
      has_access: false,
      unlimited: false,
      available_seconds: 0,
      available_minutes: 0,
      previous_minutes: 0,
      unit: 'seconds'
    };
  } catch (error) {
    console.error('Error fetching coaching access:', error);
    throw error;
  }
};

/**
 * Check if user has access to a specific feature
 * @param {string} featureCode - Feature code to check (e.g., "COACHING", "FAITH")
 * @returns {Promise<{feature_code: string, has_access: boolean}>}
 */
export const checkFeatureAccess = async (featureCode) => {
  try {
    const response = await axiosAssessmentInstance.get(`/api/functionality-access/check/${featureCode}`);
    return response.data?.payload || {
      feature_code: featureCode,
      has_access: false
    };
  } catch (error) {
    console.error(`Error checking feature access for ${featureCode}:`, error);
    throw error;
  }
};

// Feature codes enum for consistency
export const FEATURE_CODES = {
  ELEMENTS: 'ELEMENTS',
  ASSESSMENT: 'ASSESSMENT',
  CHAT: 'CHAT',
  COACHING: 'COACHING',
  SUBSCRIPTION: 'SUBSCRIPTION',
  SETTINGS: 'SETTINGS',
  CONNECTIONS: 'CONNECTIONS',
  DIALOGS_FOR_TWO: 'DIALOGS_FOR_TWO',
  FAITH: 'FAITH',
  ADMIN: 'ADMIN',
};

/**
 * Helper to check if a functionality is active
 * @param {Object} functionalities - The functionalities object from getFunctionalityAccess
 * @param {string} featureCode - The feature code to check
 * @returns {boolean}
 */
export const isFeatureActive = (functionalities, featureCode) => {
  return functionalities?.[featureCode]?.status === 'active';
};

/**
 * Helper to check if coaching is unlimited
 * @param {Object} accessData - The data from getFunctionalityAccess
 * @returns {boolean}
 */
export const hasUnlimitedCoaching = (accessData) => {
  return accessData?.has_unlimited_coaching === true;
};

/**
 * Helper to get coaching minutes from functionality access
 * @param {Object} functionalities - The functionalities object from getFunctionalityAccess
 * @returns {{available: number, unlimited: boolean}}
 */
export const getCoachingMinutesFromAccess = (functionalities) => {
  const coaching = functionalities?.COACHING?.meta_info?.COACHING;
  if (!coaching) {
    return { available: 0, unlimited: false };
  }
  
  if (coaching.unlimited) {
    return { available: Infinity, unlimited: true };
  }
  
  // Convert seconds to minutes if unit is seconds
  const value = coaching.value || 0;
  const minutes = coaching.unit === 'seconds' ? Math.floor(value / 60) : value;
  
  return { available: minutes, unlimited: false };
};
